import { apiFetch } from './client';
import type { AuditEntry } from './audit';
import type { CommandIntent } from './commands';

// The activity feed is the Log page's single read: core.audit_log (what
// happened) and core.command_intent (what was proposed) merged, filtered and
// paged by the server.
//
// The SPA used to fetch both lists and merge them in the browser, which meant
// the page could never show more than the newest 200 rows and every filter was
// really a filter over those 200 — pick a colleague whose last action was
// further back and the list came up empty.

export type FeedItemKind = 'audit' | 'command';

export interface FeedItem {
  kind: FeedItemKind;
  created_at: string;
  // Exactly one of these is set, per `kind`.
  audit?: AuditEntry;
  command?: CommandIntent;
  // Field-level before/after diff for a command row. It's stored on the
  // command_executed audit row, which the feed omits as a duplicate of the
  // intent, so the server lifts it onto the item.
  changes?: Array<Record<string, unknown>>;
}

export interface ListFeedResponse {
  items: FeedItem[];
  total: number;
}

export interface FeedActor {
  id: string;
  name: string;
  email?: string;
}

export interface FeedFiltersResponse {
  actors: FeedActor[];
  actions: string[];
}

export interface ListFeedOpts {
  limit?: number;
  offset?: number;
  actorUserIds?: string[];
  actions?: string[];
  source?: 'human' | 'ai' | '';
  createdAfter?: string; // RFC3339
  createdBefore?: string; // RFC3339
}

export async function listActivityFeed(
  token: string | null,
  slug: string,
  opts: ListFeedOpts = {},
): Promise<ListFeedResponse> {
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  for (const id of opts.actorUserIds ?? []) qs.append('actor_user_id', id);
  for (const a of opts.actions ?? []) qs.append('action', a);
  if (opts.source) qs.set('source', opts.source);
  if (opts.createdAfter) qs.set('created_after', opts.createdAfter);
  if (opts.createdBefore) qs.set('created_before', opts.createdBefore);
  const query = qs.toString();
  return apiFetch<ListFeedResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/activity${query ? `?${query}` : ''}`,
    { token },
  );
}

// The option sets the filter panel offers, derived from the org's whole
// history rather than from the rows currently on screen.
export async function listActivityFilters(
  token: string | null,
  slug: string,
): Promise<FeedFiltersResponse> {
  return apiFetch<FeedFiltersResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/activity/filters`,
    { token },
  );
}
